(() => {
  'use strict';
  if (customElements.get('edmp-welcome')) return;
  const KEY = 'edmp_welcome20_v1';
  const VISIT_KEY = 'edmp_welcome_visit_v1';
  class Welcome extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.email = this.querySelector('[name="email"]');
      this.status = this.querySelector('[data-ew-status]');
      this.firstVisit = this.isFirstVisit();
      this.bar = this.querySelector('[data-ew-bar]');
      this.busy = false;
      this.signedUp = false;
      this.elapsed = 0;
      this.requestId = crypto.randomUUID();
      const on = (el, name, fn) => el.addEventListener(name, fn, {signal:this.abort.signal});
      this.querySelectorAll('[data-ew-close]').forEach(el => on(el, 'click', () => this.close()));
      this.querySelectorAll('[data-ew-open]').forEach(el => on(el, 'click', () => this.open(true)));
      on(this.dialog, 'cancel', e => { e.preventDefault(); this.close(); });
      on(this.dialog, 'close', () => this.restoreScroll());
      on(this.dialog, 'click', e => {
        const b = this.dialog.getBoundingClientRect();
        if (e.target === this.dialog && (e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom)) this.close();
      });
      on(this.querySelector('[data-ew-signup]'), 'submit', e => this.signup(e));
      on(this.querySelector('[data-ew-survey-form]'), 'submit', e => this.survey(e));
      on(this.querySelector('[data-ew-copy]'), 'click', () => this.copy());
      on(this.querySelector('[data-ew-skip]'), 'click', () => { this.emit('survey_skipped'); this.close(); });
      on(window, 'storage', e => { if (e.key === KEY) { if (this.suppressed()) this.close(false); if (this.suppressed(false)) this.hideBar(); } });
      // A signed-in customer/event from the site's trusted identity integration wins at any time.
      on(document, 'edmp:known-customer', () => { this.blocked=true; this.hideBar(); this.remember('customer',365); this.close(false); });
      this.initialize();
    }
    disconnectedCallback() {
      this.abort?.abort(); clearInterval(this.timer); this.restoreScroll();
    }
    suppressed(includeDismissal=true) {
      try { const s = JSON.parse(localStorage.getItem(KEY) || 'null'); return s && s.until > Date.now() && (includeDismissal || s.reason !== 'dismissed'); }
      catch { return false; }
    }
    isFirstVisit() {
      // Keep the offer throughout the first visit; a later browser session is returning.
      // Fail closed when storage cannot reliably remember a visitor.
      try {
        const session = sessionStorage.getItem(VISIT_KEY);
        if (session !== null) return session === 'first';
        const first = localStorage.getItem(VISIT_KEY) === null && localStorage.getItem(KEY) === null;
        localStorage.setItem(VISIT_KEY, 'seen');
        sessionStorage.setItem(VISIT_KEY, first ? 'first' : 'returning');
        return first;
      } catch { return false; }
    }
    hideBar() { if (this.bar) this.bar.hidden=true; }
    remember(reason,days) {
      try { localStorage.setItem(KEY, JSON.stringify({reason,until:Date.now()+days*86400000})); } catch { /* Storage restrictions must not break signup. */ }
    }
    emit(name) {
      // No email, code, survey answer, or URL query string enters analytics.
      this.dispatchEvent(new CustomEvent('edmp:welcome', {bubbles:true,detail:{event:name,form:'welcome20',version:1}}));
    }
    async request(action,body,attempt=0) {
      const endpoint = this.dataset.endpoint;
      if (!endpoint?.startsWith('/apps/') || endpoint.startsWith('//')) throw new Error('unconfigured');
      const controller = new AbortController();
      const stop = () => controller.abort();
      this.abort.signal.addEventListener('abort', stop, {once:true});
      const timeout = setTimeout(stop,12000);
      try {
        const response = await fetch(endpoint+'/'+action, {
          method:body?'POST':'GET',credentials:'same-origin',signal:controller.signal,
          headers:body?{'Content-Type':'application/json','Accept':'application/json'}:{'Accept':'application/json'},
          ...(body?{body:JSON.stringify(body)}:{})
        });
        if (response.status === 409 && action === 'signup' && attempt < 5) {
          await new Promise(resolve => setTimeout(resolve,1500));
          if (this.abort.signal.aborted) throw new Error('cancelled');
          return this.request(action,body,attempt+1);
        }
        if (!response.ok) throw new Error('request_failed');
        return await response.json();
      } finally { clearTimeout(timeout); this.abort.signal.removeEventListener('abort',stop); }
    }
    async initialize() {
      if (this.dataset.enabled !== 'true' || this.suppressed(false) || /master-serum|\/ms2(?:\/|$)/i.test(location.pathname)) return;
      try {
        const raw = document.cookie.split('; ').find(x => x.startsWith('__kla_id='))?.slice(9);
        if (raw && JSON.parse(atob(decodeURIComponent(raw))).$exchange_id) { this.remember('customer',365); return; }
      } catch { /* An unreadable identity cookie is not a verified identity. */ }
      try {
        const eligibility = await this.request('eligibility?page='+encodeURIComponent(location.pathname));
        if (eligibility.allowPopup !== true || this.blocked || this.suppressed(false) || !this.isConnected) return;
        this.eligibilityToken = eligibility.token;
        // Keep the same signed ticket across reloads while a signup is processing.
        // This stores no email or customer identity and expires before the server ticket.
        try {
          const cached = JSON.parse(sessionStorage.getItem('edmp_welcome_ticket_v1') || 'null');
          if (cached?.until > Date.now() && typeof cached.token === 'string') this.eligibilityToken = cached.token;
          else if (this.eligibilityToken) sessionStorage.setItem('edmp_welcome_ticket_v1',JSON.stringify({token:this.eligibilityToken,until:Date.now()+25*60*1000}));
        } catch { /* In-page retries still work when storage is unavailable. */ }
        if (!this.eligibilityToken) return;
        if (this.bar && this.firstVisit) this.bar.hidden=false;
        if (this.suppressed()) return;
        // No display while the page is hidden or another shopping dialog is open.
        this.timer = setInterval(() => {
          const otherDialog = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(el => el.getClientRects().length > 0);
          if (!document.hidden && !otherDialog) this.elapsed += 1;
          if (this.elapsed >= Number(this.dataset.delay || 20)) { clearInterval(this.timer); this.open(); }
        },1000);
      } catch { /* Fail closed: shopping continues without the offer. */ }
    }
    open(manual=false) {
      if (this.blocked || this.suppressed(!manual) || !this.eligibilityToken || this.dialog.open || this.dataset.enabled !== 'true' || !this.isConnected) return;
      clearInterval(this.timer);
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden'; this.ownsScroll = true;
      this.dialog.showModal(); this.email.focus({preventScroll:true}); this.emit('displayed');
    }
    restoreScroll() {
      if (this.ownsScroll) { document.documentElement.style.overflow = this.previousOverflow; this.ownsScroll = false; }
    }
    close(persist=true) {
      if (persist && !this.signedUp) this.remember('dismissed',Number(this.dataset.dismissDays || 7));
      if (this.dialog.open) { this.dialog.close(); this.emit('closed'); }
      this.restoreScroll();
      if (this.previousFocus?.isConnected) this.previousFocus.focus({preventScroll:true});
    }
    error(selector,message) { const el=this.querySelector(selector); el.textContent=message; el.hidden=!message; }
    async signup(e) {
      e.preventDefault(); if (this.busy || this.signedUp || !e.target.reportValidity()) return;
      this.busy=true; const button=this.querySelector('[data-ew-submit]'); button.disabled=true; button.textContent='Getting your code…';
      this.error('#ew-error','');
      try {
        const result=await this.request('signup',{
          email:this.email.value.trim(), consent:true, consentVersion:'welcome20-v1',
          website:new FormData(e.target).get('website'), requestId:this.requestId,
          token:this.eligibilityToken, page:location.pathname
        });
        if (result.status === 'ineligible') {
          this.remember('customer',365); this.hideBar();
          this.querySelector('[data-ew-entry]').hidden=true;
          this.querySelector('[data-ew-notice]').hidden=false;
          this.dialog.setAttribute('aria-labelledby','ew-notice-title');
          this.querySelector('#ew-notice-title').focus(); this.emit('ineligible'); return;
        }
        if (result.status !== 'accepted' || typeof result.code !== 'string' || !/^[A-Z0-9_-]{3,40}$/.test(result.code) || !result.receipt) throw new Error('not_accepted');
        this.signedUp=true; this.hideBar(); this.remember('signed_up',365); this.receipt=result.receipt;
        this.querySelector('[data-ew-code]').textContent=result.code;
        this.querySelector('[data-ew-delivery]').textContent=result.emailStatus==='confirmation_required'
          ? 'Your code is ready. Confirm your subscription in your inbox to receive welcome emails.'
          : 'Your code is ready. We’ll also email it to you.';
        this.email.value='';
        this.querySelector('[data-ew-entry]').hidden=true; this.querySelector('[data-ew-success]').hidden=false;
        this.dialog.setAttribute('aria-labelledby','ew-success-title');
        if(this.dialog.open) { this.dialog.scrollTop=0; this.querySelector('#ew-success-title').focus({preventScroll:true}); }
        this.emit('signup_accepted'); this.emit('survey_displayed');
      } catch { this.error('#ew-error','We couldn’t confirm your signup. Please try again.'); this.emit('signup_error'); }
      finally { this.busy=false; button.disabled=false; button.textContent='Get my 20% discount'; }
    }
    async copy() {
      const code=this.querySelector('[data-ew-code]').textContent;
      const button=this.querySelector('[data-ew-copy]');
      try { await navigator.clipboard.writeText(code); button.textContent='Copied ✓'; this.status.textContent='Discount code copied.'; this.emit('code_copied'); }
      catch {
        const selection=getSelection(),range=document.createRange(); range.selectNodeContents(this.querySelector('[data-ew-code]')); selection.removeAllRanges(); selection.addRange(range);
        this.status.textContent='Select and copy the highlighted code.'; button.textContent='Select & copy';
      }
    }
    async survey(e) {
      e.preventDefault(); if (this.surveyBusy || !this.receipt) return;
      const answer=new FormData(e.target).get('intent');
      if(typeof answer !== 'string' || !answer.trim() || answer.length > 2000) { this.error('[data-ew-survey-error]','Write a response (up to 2,000 characters), or skip below.'); return; }
      const button=this.querySelector('[data-ew-save]'); this.surveyBusy=true; button.disabled=true;
      this.error('[data-ew-survey-error]','');
      try {
        const result=await this.request('survey',{receipt:this.receipt,question:'shopping_intent_v2',answer});
        if(result.saved!==true) throw new Error('not_saved');
        this.emit('survey_saved'); this.close();
      } catch { this.error('[data-ew-survey-error]','Your code is still ready to use. We couldn’t save your answer—retry or skip.'); }
      finally { this.surveyBusy=false; button.disabled=false; }
    }
  }
  customElements.define('edmp-welcome',Welcome);
})();
